import { useState } from "react";
import { IonHeader, IonToolbar, IonButtons, IonButton, IonTitle, IonContent, IonList, IonItem, IonLabel, IonPage, IonIcon, IonRadioGroup, IonRadio, useIonAlert } from "@ionic/react";
import { close, cloudDownloadOutline, pricetagOutline, shieldCheckmarkOutline, sparklesOutline } from "ionicons/icons";
import { useDeviceQuery } from "../query/deviceQuery";
import { useCurrentUserQuery } from "../query/currentUserQuery";
import { useDeviceFirmwareChannelMutation } from "../query/deviceFirmwareChannelMutation";
import useUX from '../utils/ux';
import BetaChip from '../components/BetaChip';
import ModalFooter from '../components/ModalFooter';
import footerStyles from '../components/ModalFooter.module.css';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import { Trans } from 'react-i18next';
import styles from './DeviceFirmwareChannelModal.module.css';

const CHANNELS = [
    {
        id: 'standard',
        icon: shieldCheckmarkOutline,
        labelKey: 'firmware_channel.standard_label',
        noteKey: 'firmware_channel.standard_note',
        beta: false,
    },
    {
        id: 'experimental',
        icon: sparklesOutline,
        labelKey: 'firmware_channel.experimental_label',
        noteKey: 'firmware_channel.experimental_note',
        beta: true,
    },
] as const;

const DeviceFirmwareChannelModal = ({ dismiss, deviceId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceId: string }) => {
    const deviceQuery = useDeviceQuery(deviceId);
    const deviceMutation = useDeviceFirmwareChannelMutation(deviceId);
    const currentUserQuery = useCurrentUserQuery();
    const { presentResult } = useUX();
    const { t } = useModalTranslation();
    const { t: tCommon } = useCommonTranslation();
    const [presentAlert] = useIonAlert();
    const [selectedChannel, setSelectedChannel] = useState(deviceQuery.data?.firmwareChannel || 'standard');
    // Initialized from the device so the row exists on first render: items
    // inserted while ion-radio-group is still hydrating end up re-parented
    // out of order.
    const [customChannel, setCustomChannel] = useState<string | undefined>(() => {
        const channel = deviceQuery.data?.firmwareChannel;
        return channel && !['standard', 'experimental'].includes(channel) ? channel : undefined;
    });

    const isAdmin = currentUserQuery.data?.userLevel === 'ADMIN';
    const currentChannel = deviceQuery.data?.firmwareChannel || 'standard';

    const saveFirmwareChannel = async () => {
        presentResult(async () => {
            await deviceMutation.mutateAsync(selectedChannel);
            dismiss(selectedChannel, 'select');
        }, t('firmware_channel.updated_toast'));
    };

    const promptCustomChannel = () => {
        presentAlert({
            header: t('firmware_channel.custom_prompt.header'),
            message: t('firmware_channel.custom_prompt.message'),
            inputs: [
                {
                    name: 'channel',
                    type: 'text',
                    placeholder: t('firmware_channel.custom_prompt.placeholder'),
                    value: customChannel
                }
            ],
            buttons: [
                {
                    text: tCommon('buttons.cancel'),
                    role: 'cancel'
                },
                {
                    text: tCommon('buttons.ok'),
                    handler: (data) => {
                        const channel = data.channel?.trim().toLowerCase();
                        if (!channel) {
                            return false;
                        }
                        if (!['standard', 'experimental'].includes(channel)) {
                            setCustomChannel(channel);
                        }
                        setSelectedChannel(channel);
                    }
                }
            ]
        });
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle>{t('firmware_channel.title')}</IonTitle>
                    <IonButtons slot="primary">
                        <IonButton onClick={() => dismiss(null, 'cancel')} aria-label={t('firmware_channel.close')}>
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent color="light">
                <div className={styles.wrapper}>
                    <div className={styles.introCard}>
                        <span className={styles.introIcon} aria-hidden="true">
                            <IonIcon icon={cloudDownloadOutline} />
                        </span>
                        <div className={styles.introBody}>
                            <h2 className={styles.introTitle}>{t('firmware_channel.intro_title')}</h2>
                            <p className={styles.introText}>
                                <Trans i18nKey="firmware_channel.standard_info" ns="modals" components={{ strong: <strong /> }} />
                                {' '}
                                <Trans i18nKey="firmware_channel.experimental_info" ns="modals" components={{ strong: <strong /> }} />
                            </p>
                        </div>
                    </div>

                    <section className={styles.section}>
                        <h3 className={styles.sectionLabel}>{t('firmware_channel.section_channel')}</h3>
                        <IonList className={styles.listCard}>
                            <IonRadioGroup value={selectedChannel} onIonChange={e => setSelectedChannel(e.detail.value)}>
                                {CHANNELS.map((channel) => (
                                    <IonItem key={channel.id} className={selectedChannel === channel.id ? styles.rowSelected : undefined}>
                                        <span slot="start" className={styles.rowIcon} aria-hidden="true">
                                            <IonIcon icon={channel.icon} />
                                        </span>
                                        <IonRadio value={channel.id} labelPlacement="start" justify="space-between">
                                            <IonLabel>
                                                <h2 className={styles.rowTitle}>
                                                    {t(channel.labelKey)}
                                                    {channel.beta && <span className={styles.beta}><BetaChip /></span>}
                                                </h2>
                                                <p>{t(channel.noteKey)}</p>
                                            </IonLabel>
                                        </IonRadio>
                                    </IonItem>
                                ))}
                                {customChannel && (
                                    <IonItem className={selectedChannel === customChannel ? styles.rowSelected : undefined}>
                                        <span slot="start" className={styles.rowIcon} aria-hidden="true">
                                            <IonIcon icon={pricetagOutline} />
                                        </span>
                                        <IonRadio value={customChannel} labelPlacement="start" justify="space-between">
                                            <IonLabel>
                                                <h2 className={`${styles.rowTitle} ${styles.capitalize}`}>{customChannel}</h2>
                                                <p>{t('firmware_channel.custom_note')}</p>
                                            </IonLabel>
                                        </IonRadio>
                                    </IonItem>
                                )}
                            </IonRadioGroup>
                            {isAdmin && (
                                <IonItem button detail onClick={promptCustomChannel}>
                                    <span slot="start" className={styles.rowIcon} aria-hidden="true">
                                        <IonIcon icon={pricetagOutline} />
                                    </span>
                                    <IonLabel>
                                        <h2 className={styles.rowTitle}>{t('firmware_channel.other_label')}</h2>
                                        <p>{t('firmware_channel.other_note')}</p>
                                    </IonLabel>
                                </IonItem>
                            )}
                        </IonList>
                    </section>
                </div>
            </IonContent>
            <ModalFooter>
                <IonButton
                    expand="block"
                    className={footerStyles.primaryButton}
                    onClick={saveFirmwareChannel}
                    disabled={selectedChannel === currentChannel || deviceMutation.isPending}
                >
                    {tCommon('buttons.save')}
                </IonButton>
            </ModalFooter>
        </IonPage>
    );
};

export default DeviceFirmwareChannelModal;
